import * as Cesium from "cesium";

// ─── Footer Tabs ────────────────────────────────────────────────────
export type FooterTab = 'hud' | 'monitor';

// ─── WebSocket Message Types ────────────────────────────────────────
export type WSCommandName = 'RepElementFixedState' | 'RepGetLinkStats';

/** Incoming WS frame for RepElementFixedState — ECEF position + velocity + quaternion */
export interface RepElementFixedStatePayload {
    C2__destination_id: number;
    C2__rel_time_ms: number;
    C2__request_id: number;
    C2__request_type: number;
    C2__schema_message_id: number;
    C2__source_id: number;
    C2__zone_id: number;
    RepElementFixedState__pos_x: number;
    RepElementFixedState__pos_y: number;
    RepElementFixedState__pos_z: number;
    RepElementFixedState__q_1: number;
    RepElementFixedState__q_2: number;
    RepElementFixedState__q_3: number;
    RepElementFixedState__q_4: number;
    RepElementFixedState__vel_x: number;
    RepElementFixedState__vel_y: number;
    RepElementFixedState__vel_z: number;
}

/** Incoming WS frame for RepGetLinkStats — peer link statistics */
export interface RepGetLinkStatsPayload {
    C2__destination_id: number;
    C2__rel_time_ms: number;
    C2__request_id: number;
    C2__request_type: number;
    C2__schema_message_id: number;
    C2__source_id: number;
    C2__zone_id: number;
    RepGetLinkStats__active_peers: number[];
    RepGetLinkStats__msg_rx_bytes: number[];
    RepGetLinkStats__msg_rx_count: number[];
    RepGetLinkStats__msg_tx_bytes: number[];
    RepGetLinkStats__msg_tx_count: number[];
    array_RepGetLinkStats__active_peers__size: number;
    array_RepGetLinkStats__msg_rx_bytes__size: number;
    array_RepGetLinkStats__msg_rx_count__size: number;
    array_RepGetLinkStats__msg_tx_bytes__size: number;
    array_RepGetLinkStats__msg_tx_count__size: number;
}

/** Generic WS message envelope */
export interface WSMessage {
    commandName: WSCommandName;
    elementId: string;
    elementName: string;
    payload: RepElementFixedStatePayload | RepGetLinkStatsPayload;
    queueName: string;
    scenarioId: string;
    timestamp: number;
    trueTwinId: string;
}

// ─── ECEF State (core telemetry record) ─────────────────────────────
export interface ECEFState {
    pos_x: number;
    pos_y: number;
    pos_z: number;
    vel_x: number;
    vel_y: number;
    vel_z: number;
    q_1: number;
    q_2: number;
    q_3: number;
    q_4: number;
    timestamp: number;
}

/** Parsed position data extracted from RepElementFixedState */
export interface ElementPositionData {
    elementId: string;
    elementName: string;
    timestamp: number;
    position: { x: number; y: number; z: number };
    velocity: { x: number; y: number; z: number };
    quaternion: { q1: number; q2: number; q3: number; q4: number };
}

// ─── Element Tracking Map ───────────────────────────────────────────
export type ElementType = 'satellite' | 'groundstation' | 'missile' | 'aircraft' | 'vessel' | 'unknown';

/** Trajectory element types (leave trails) */
export const TRAJECTORY_TYPES: ElementType[] = ['missile', 'aircraft', 'vessel'];

/** Orbital parameters from deployment data */
export interface OrbitParams {
    altitude: number;
    eccentricity: number;
    inclination: number;
    RAAN: number;
    AP: number;
    TA: number;
    epoch: number;
}

/** Ground station location (geodetic) */
export interface EarthLocation {
    latitude: number;
    longitude: number;
    altitude: number;
}

export interface ElementInfo {
    elementId: string;
    elementName: string;
    elementType: ElementType;
    groupColor?: string;
    groupName?: string;
    satelliteId?: string;
    orbitParameters?: OrbitParams;
    location?: EarthLocation;
    nodeId?: number;
}

export type ElementMap = Record<string, ElementInfo>;

// ─── Trail Point ────────────────────────────────────────────────────
export interface TrailPoint {
    x: number;
    y: number;
    z: number;
    timestamp: number;
}

// ─── Link Stats ─────────────────────────────────────────────────────
export interface LinkStatsData {
    elementId: string;
    activePeers: number[];
    timestamp: number;
}

// ─── Visibility Toggle Settings ─────────────────────────────────────
export interface VisibilitySettings {
    globalEnabled: boolean;
    perElement: Record<string, boolean>;
}

export interface VisualizationSettings {
    elementVisibility: VisibilitySettings;
    orbitTracks: VisibilitySettings;
    fovCones: VisibilitySettings;
    forCones: VisibilitySettings;
    trajectoryTrails: VisibilitySettings;
    orientationAxes: VisibilitySettings;
    commLines: VisibilitySettings;
}

// ─── Selected Element ───────────────────────────────────────────────
export interface SelectedElementInfo {
    elementId: string;
    elementName: string;
    elementType: ElementType;
    altitude?: number;
    speed?: number;
    position?: { x: number; y: number; z: number };
    velocity?: { x: number; y: number; z: number };
    quaternion?: { q1: number; q2: number; q3: number; q4: number };
    eulerAngles?: { roll: number; pitch: number; yaw: number };
}

// ─── Summary / Replay ───────────────────────────────────────────────
/** A single snapshot from the visualization API for one element */
export interface VisualizationStateEntry {
    timestamp: number;
    rel_time_ms: number;
    position: { pos_x: number; pos_y: number; pos_z: number };
    velocity: { vel_x: number; vel_y: number; vel_z: number };
    orientation: { q_1: number; q_2: number; q_3: number; q_4: number };
}

/** One element from the visualization API response */
export interface VisualizationElement {
    elementId: string;
    elementName: string;
    trueTwinId: string;
    elementStateData: VisualizationStateEntry[];
}

/** Full visualization API response payload (inside `data`) */
export interface VisualizationData {
    scenarioId: string;
    elements: VisualizationElement[];
    totalElements: number;
    returnedElements: number;
}

/** Replay playback state */
export type ReplayStatus = 'idle' | 'playing' | 'paused';

export interface ReplayState {
    status: ReplayStatus;
    /** Current replay time in ms (rel_time_ms) */
    currentTimeMs: number;
    /** Global min rel_time_ms across all elements */
    startTimeMs: number;
    /** Global max rel_time_ms across all elements */
    endTimeMs: number;
    /** Playback speed multiplier (1 = real-time, 2 = 2x, etc.) */
    speed: number;
}

// ─── Modal Types ────────────────────────────────────────────────────
export type Modals = {
    name: string;
    open: boolean;
    pinned: boolean;
    current_modal: string;
};

export type ModalType = 'generic_simulator_console';

// ─── Context Type ───────────────────────────────────────────────────
export type GenericSimulatorContextType = {
    viewer: Cesium.Viewer | null;
    setViewer: (viewer: Cesium.Viewer | null) => void;

    activeTab: FooterTab;
    setActiveTab: (tab: FooterTab) => void;

    scenarioData: any;
    setScenarioData: (data: any) => void;

    loading: boolean;
    setLoading: (loading: boolean) => void;

    socket: WebSocket | null;
    setSocket: (socket: WebSocket | null) => void;

    deploymentData: any;
    setDeploymentData: (data: any) => void;

    elementMap: ElementMap;
    setElementMap: (map: ElementMap) => void;

    visualizationSettings: VisualizationSettings;
    setVisualizationSettings: React.Dispatch<React.SetStateAction<VisualizationSettings>>;

    selectedElement: SelectedElementInfo | null;
    setSelectedElement: React.Dispatch<React.SetStateAction<SelectedElementInfo | null>>;

    nodeIdToElementId: Record<number, string>;
    setNodeIdToElementId: React.Dispatch<React.SetStateAction<Record<number, string>>>;

    linkStatsMap: Record<string, LinkStatsData>;
    setLinkStatsMap: React.Dispatch<React.SetStateAction<Record<string, LinkStatsData>>>;

    trailsMap: Record<string, TrailPoint[]>;
    setTrailsMap: React.Dispatch<React.SetStateAction<Record<string, TrailPoint[]>>>;

    elementHistoryMap: Record<string, ECEFState[]>;
    setElementHistoryMap: React.Dispatch<React.SetStateAction<Record<string, ECEFState[]>>>;

    grafanaData: any;
    setGrafanaData: React.Dispatch<React.SetStateAction<any>>;

    /** Raw visualization data from summary API */
    visualizationData: VisualizationData | null;
    setVisualizationData: React.Dispatch<React.SetStateAction<VisualizationData | null>>;

    /** Replay playback state */
    replayState: ReplayState;
    setReplayState: React.Dispatch<React.SetStateAction<ReplayState>>;

    /** Modals state (console, etc.) */
    modals: Modals[];
    set_Modals: React.Dispatch<React.SetStateAction<Modals[]>>;

    /** Console filter options from API */
    consoleFilterOptions: any;
    setConsoleFilterOptions: React.Dispatch<React.SetStateAction<any>>;

    /** Highlighted command UIDs in console */
    highlightedCommands: Set<string>;
    setHighlightedCommands: React.Dispatch<React.SetStateAction<Set<string>>>;
};
